import React, { useState, useEffect } from 'react';
import AOS from 'aos';
import './NewsAndEventsStyles.css';
import { FaCalendarAlt, FaClock, FaMapMarkerAlt } from 'react-icons/fa';
import CommonButtonView from '../../../components/CommonButton/View/CommonButtonView';
import { EventsData } from '../Model/NewsAndEventsModel';

const NewsAndEventsView = () => {
    const [timeLeft, setTimeLeft] = useState({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
    });
    const upcomingEvent = EventsData.find(event => event.isUpcoming);
    
    // Helper to get event status
    const getStatus = (event, currentTime) => {
        if (!event) return 'concluded';
        const start = new Date(event.startDate).getTime();
        const end = new Date(event.endDate).getTime();
        if (currentTime > end) return 'concluded';
        if (currentTime >= start) return 'live';
        return 'upcoming';
    };

    const initialNow = new Date().getTime();
    const [eventStatus, setEventStatus] = useState(() => getStatus(upcomingEvent, initialNow));
    
    // Filter past events for the grid (include the main event if it's concluded)
    const pastEvents = EventsData.filter(event => {
        const eventEndTime = new Date(event.endDate).getTime();
        return initialNow > eventEndTime;
    }).sort((a, b) => new Date(b.startDate) - new Date(a.startDate));

    useEffect(() => {
        if (!upcomingEvent || eventStatus === 'concluded') return;

        const timer = setInterval(() => {
            const currentTime = new Date().getTime();
            const currentStatus = getStatus(upcomingEvent, currentTime);
            
            if (currentStatus !== eventStatus) {
                setEventStatus(currentStatus);
                if (currentStatus === 'concluded' || currentStatus === 'live') {
                    clearInterval(timer);
                    return;
                }
            }

            if (currentStatus === 'upcoming') {
                const startDate = new Date(upcomingEvent.startDate).getTime();
                const distance = startDate - currentTime;

                setTimeLeft({
                    days: Math.floor(distance / (1000 * 60 * 60 * 24)),
                    hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
                    minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
                    seconds: Math.floor((distance % (1000 * 60)) / 1000)
                });
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [upcomingEvent, eventStatus]);

    useEffect(() => {
        AOS.refresh();
    }, []);

    return (
        <div className="news-events-container">
            <div className="news-events-header" data-aos="fade-up">
                <h1>News & <span className="highlight">Events</span></h1>
                <p>Stay updated with our latest milestones and upcoming gatherings.</p>
            </div>

            {upcomingEvent && eventStatus !== 'concluded' && (
                <div className="upcoming-events-section">
                    <div className="section-title-wrap" data-aos="fade-up">
                        <h2>Upcoming <span className="highlight">Events</span></h2>
                    </div>
                    <div className={`event-banner status-${eventStatus}`} data-aos="zoom-in">
                        <div className="event-banner-overlay">
                            <div className="event-info-left" data-aos="fade-right" data-aos-delay="200">
                                <h3>{upcomingEvent.title}</h3>
                                <p className="event-subtitle">{upcomingEvent.subtitle}</p>
                                <div className="event-meta">
                                    <span><FaCalendarAlt className="meta-icon" /> {upcomingEvent.displayDate}</span>
                                    <span><FaClock className="meta-icon" /> {upcomingEvent.startTime}</span>
                                    <span><FaMapMarkerAlt className="meta-icon" /> {upcomingEvent.location}</span>
                                </div>
                                <p className="event-desc-main">{upcomingEvent.description}</p>
                                {eventStatus !== 'concluded' && (
                                    <div className='header-view-main-button-container-wrapup'>
                                        <CommonButtonView 
                                            name={eventStatus === 'live' ? "Join Event" : "Register Now"} 
                                            Todo={() => window.open(upcomingEvent.link, '_blank')} 
                                        />
                                    </div>
                                )}
                            </div>

                            <div className={`event-timer-right status-${eventStatus}`} data-aos="fade-left" data-aos-delay="400">
                                {eventStatus === 'upcoming' && (
                                    <>
                                        <div className="timer-title">Event Starts In</div>
                                        <div className="timer-grid">
                                            <div className="timer-item">
                                                <span className="time-value">{timeLeft.days}</span>
                                                <span className="time-label">Days</span>
                                            </div>
                                            <div className="timer-item">
                                                <span className="time-value">{timeLeft.hours}</span>
                                                <span className="time-label">Hrs</span>
                                            </div>
                                            <div className="timer-item">
                                                <span className="time-value">{timeLeft.minutes}</span>
                                                <span className="time-label">Min</span>
                                            </div>
                                            <div className="timer-item">
                                                <span className="time-value">{timeLeft.seconds}</span>
                                                <span className="time-label">Sec</span>
                                            </div>
                                        </div>
                                    </>
                                )}
                                {eventStatus === 'live' && (
                                    <div className="live-status-container">
                                        <div className="live-badge">
                                            <span className="pulse-dot"></span>
                                            LIVE NOW
                                        </div>
                                        <p className="live-text">The event is currently in progress. Join us at the venue!</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="past-events-section">
                <div className="section-title-wrap" data-aos="fade-up">
                    <h2>Latest <span className="highlight">Updates</span></h2>
                </div>
                
                <div className="news-cards-grid">
                    {pastEvents.map((event, index) => (
                        <div key={event.id} className="custom-news-card" data-aos="fade-up" data-aos-delay={index * 100}>
                            <div 
                                className="news-card-image" 
                                style={{ backgroundImage: `url(${event.image})` }}
                            ></div>
                            <div className="news-card-content">
                                <div className="news-card-meta">
                                    <span className="news-card-date">{event.displayDate}</span>
                                    {event.subtitle && (
                                        <span className="news-card-location">
                                            <FaMapMarkerAlt className="card-location-icon" /> {event.subtitle}
                                        </span>
                                    )}
                                </div>
                                <h4>{event.title}</h4>
                                <p>{event.description}</p>
                                <a href={event.link} target="_blank" rel="noopener noreferrer" className="read-more-link">
                                    {event.link.includes('instagram.com') ? 'Read More on Instagram →' : 'Read More on LinkedIn →'}
                                </a>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default NewsAndEventsView;
